import { useTranslation } from 'react-i18next'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { ProcessorDetails } from '@/lib/api/processors'
import { formatMoney } from '@/lib/money'
import { Badge } from '@/components/ui/badge'

interface AdyenConfigurationCardProps {
    processor: ProcessorDetails
}

const THREE_DS_MODE_LABELS: Record<string, string> = {
    sca_only: 'SCA only (EEA + UK)',
    always: 'Always',
    never: 'Never',
    prefer_no: 'Prefer no challenge',
    prefer_yes: 'Prefer challenge',
}

const API_KEY_LABEL = 'API Key'
const CLIENT_KEY_LABEL = 'Client Key'
const HMAC_KEY_LABEL = 'HMAC Key'
const LIVE_ENDPOINT_PREFIX_LABEL = 'Live Endpoint Prefix'
const TEST_ONLY = 'Test only'
const MODE_LABEL = 'Mode'
const TEST_LABEL = 'Test'
const LIVE_LABEL = 'Live'
const THREE_DS_LABEL = '3D Secure'

const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    })
}

export const AdyenConfigurationCard = ({ processor }: AdyenConfigurationCardProps) => {
    const { t } = useTranslation('processors')
    const options = processor.options as Record<string, any> | null

    return (
        <Card className="mb-6">
            <CardHeader>
                <CardTitle>{t('adyen.configuration.title')}</CardTitle>
            </CardHeader>
            <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <p className="text-sm text-muted-foreground">{t('merchantAccount')}</p>
                        <p className="font-mono text-sm">{options?.merchantAccount || t('notConfigured')}</p>
                    </div>

                    <div>
                        <p className="text-sm text-muted-foreground">{API_KEY_LABEL}</p>
                        <p className="font-mono text-sm">••••••••••••••••</p>
                    </div>

                    <div>
                        <p className="text-sm text-muted-foreground">{CLIENT_KEY_LABEL}</p>
                        <p className="font-mono text-sm">{options?.clientKey || t('notConfigured')}</p>
                    </div>

                    <div>
                        <p className="text-sm text-muted-foreground">{HMAC_KEY_LABEL}</p>
                        <p className="font-mono text-sm">{options?.hmacKey ? '••••••••••••••••' : t('notConfigured')}</p>
                    </div>

                    <div>
                        <p className="text-sm text-muted-foreground">{LIVE_ENDPOINT_PREFIX_LABEL}</p>
                        <p className="font-mono text-sm">{options?.liveEndpointPrefix || TEST_ONLY}</p>
                    </div>

                    <div>
                        <p className="text-sm text-muted-foreground">{MODE_LABEL}</p>
                        <Badge variant={options?.testMode ? 'outline' : 'default'}>
                            {options?.testMode ? TEST_LABEL : LIVE_LABEL}
                        </Badge>
                    </div>

                    <div>
                        <p className="text-sm text-muted-foreground">{THREE_DS_LABEL}</p>
                        <p className="font-medium">
                            {THREE_DS_MODE_LABELS[options?.threeDSMode ?? 'sca_only'] ?? options?.threeDSMode}
                        </p>
                    </div>

                    {processor.baseCurrency && (
                        <div>
                            <p className="text-sm text-muted-foreground">{t('baseCurrency')}</p>
                            <p className="font-semibold">{processor.baseCurrency}</p>
                        </div>
                    )}

                    {processor.monthlyProcessingCapacity !== null && (
                        <div>
                            <p className="text-sm text-muted-foreground">{t('monthlyCapacity')}</p>
                            <p className="font-semibold">
                                {formatMoney(processor.monthlyProcessingCapacity, processor.baseCurrency || 'USD')}
                            </p>
                        </div>
                    )}

                    <div>
                        <p className="text-sm text-muted-foreground">{t('createdAt')}</p>
                        <p className="font-semibold">{formatDate(processor.createdAt)}</p>
                    </div>

                    <div>
                        <p className="text-sm text-muted-foreground">{t('updatedAt')}</p>
                        <p className="font-semibold">{formatDate(processor.updatedAt)}</p>
                    </div>

                    {processor.supportedCurrencies && processor.supportedCurrencies.length > 0 && (
                        <div className="col-span-full">
                            <p className="text-sm text-muted-foreground mb-2">{t('supportedCurrencies')}</p>
                            <div className="flex flex-wrap gap-2">
                                {processor.supportedCurrencies.map((currency) => (
                                    <Badge key={currency} variant="outline">
                                        {currency}
                                    </Badge>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </CardContent>
        </Card>
    )
}
